import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import StartSettings from "../components/startSettings";
//React Slick Imports
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import pageTransition from "../components/pageTransition";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import MetaHelmet from "../components/metaHelmet";
import LoadingScreen from "../components/loadingScreen";
import { shareUrl } from "../helper/Data";


interface GalleryProps {
    venuesLoaded: boolean;
}

interface Venue {
    id: number;
    title: string;
    slug: string;
    category: number[];
    parent_category: number[];
    restaurant__gallery: string[];
    restaurant__closed: {
        from: string;
        until: string;
    };
    restaurant__mittagsteller: string;
    restaurant__website: string;
    restaurant__menu: string;
    restaurant__gps_lon: string;
    restaurant__gps_lat: string;
    restaurant__name: string;
    restaurant__opening_hours: {
        "restaurant__opening_hours-open": string;
        "restaurant__opening_hours-close": string;
        "restaurant__opening_hours-dow_till": string;
        "restaurant__opening_hours-warm_cuisine": boolean;
        "restaurant__opening_hours-dow_from": string;
    }[];
    restaurant__email: string;
    restaurant__phone_number: string;
    restaurant__address: string;
    restaurant__google_key: string;
    restaurant__views: string;
    google_key: string;
}

interface PartnerCategory {
    partner_category: number;
    partner_category_picture: string;
}

interface PartnerSocialMedia {
    partner_social_media_link: string;
}

interface Partner {
    id: number;
    venue_id: number;
    venue_slug: string;
    type: string; // "prm" or "std"
    categories: PartnerCategory[];
    social_media: PartnerSocialMedia[];
    description_de: string;
    description_en: string;
    post_slug: string;
    reservation_link: string;
}



const Gallery: React.FC<GalleryProps> = ({ venuesLoaded }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Use `useLocation` to extract venue
    const location = useLocation();
    const internalOrigin = location.state?.fromInternal || false;
    const pathname = location.pathname.endsWith('/')
        ? location.pathname.slice(0, -1)
        : location.pathname;
    const segments = pathname.substring(1).split('/');
    const venueSlug = segments.length > 1 ? segments[segments.length - 2] : null;
    const venuePath = segments.slice(0, -1).join('/');

    const { trackPageView } = useMatomo();

    // Load settings
    const [settingsSet] = useState<boolean>(() => !!localStorage.getItem('settings-set'));

    const [venue, setVenue] = useState<Venue | null>(null);
    const [gallery, setGallery] = useState<string[]>([]);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
    const [isPartner, setIsPartner] = useState(false);
    const [partnerValues, setPartnerValues] = useState<Partner | null>(null);
    const sliderRef = React.useRef<Slider | null>(null);

    const gallerySliderSettings = {
        dots: true,           // Dots anzeigen
        infinite: true,       // Keine Endlosschleife
        slidesToShow: 1,       // Nur eine Slide anzeigen
        slidesToScroll: 1,
        autoplay: false,       // Kein automatisches Weiterblättern
        arrows: true,           // Pfeile für die Navigation (optional)
        swipe: true,
    };


    ////// Helper Functions /////// 


    function getVenueBySlug(slug: string, venues: Venue[]): Venue | null {
        const foundVenue = venues.find((venue) => venue.slug === slug);
        return foundVenue || null;
    }


    ////// Page useEffect ///////   
    useEffect(() => {
        if (!venuesLoaded) return;
        trackPageView({
            href: window.location.href, // URL der aktuellen Seite
        });

        let tempVenues = localStorage.getItem('venues');
        if (tempVenues) {
            let venues = JSON.parse(tempVenues);
            const currentVenue = getVenueBySlug(venueSlug || '', venues);

            if (currentVenue) {
                setVenue(currentVenue);
                setGallery(currentVenue.restaurant__gallery);

                // Load partner data
                const partnersJson = localStorage.getItem("partner");
                if (partnersJson) {
                    const partnerVenues: Partner[] = JSON.parse(partnersJson);

                    const matchedPartner = partnerVenues.find(
                        (partner) => partner.venue_id === currentVenue.id
                    );

                    if (matchedPartner) {
                        setIsPartner(true);
                        setPartnerValues(matchedPartner);
                    }
                }
            }
            else {
                navigate('/notfound');
            }
        }
    }, [location, venuesLoaded]);


    ////// Return Statement ///////   
    return (
        !settingsSet ? (
            <ion-page class="sg-body">
                <StartSettings />
            </ion-page>
        ) : (
            venuesLoaded && venue ? (
                <ion-page class="sg-body gallery">
                    <div className="page-container">
                        <MetaHelmet
                            title={venue.title + ' - Graz'}
                            desc={`Alle Details zu ${venue.title} in Graz findest du auf smartGastro. Entdecke Öffnungszeiten, Speisekarten und mehr!`}
                        />
                        <ion-header mode="ios">
                            <div className="header-container gallery venue ion-padding">
                                {internalOrigin && <button className="arrow-back" style={{ backgroundImage: `url("/icons/back_arrow_white.svg")` }} onClick={() => navigate(-1)}></button>}
                                {!internalOrigin && <button className="arrow-back" style={{ backgroundImage: `url("/icons/back_arrow_white.svg")` }} onClick={() => navigate('/betriebe/' + venueSlug)}></button>}
                                <h1 className="start-title left">{venue.title}
                                    {isPartner &&
                                        (
                                            <span className="partner" />
                                        )}
                                </h1>
                                <span onClick={() => shareUrl()} className="share-icon"></span>
                            </div>
                        </ion-header>
                        <div className="gallery-container">
                            {gallery.length > 0 && (
                                <>
                                    {/* Photo Grid Übersicht */}
                                    <div className="gallery-grid">
                                        {gallery.map((src, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    className="gallery-item"
                                                    onClick={() => {
                                                        setSelectedImageIndex(index);
                                                        setModalOpen(true);
                                                    }}
                                                >
                                                    <img src={src} alt={`Gallery ${index + 1}`} />
                                                    <div className="overlay"></div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Modal mit Slider */}
                                    {modalOpen && (
                                        <div className="gallery-modal" onClick={() => setModalOpen(false)}>
                                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    className="modal-close"
                                                    onClick={() => setModalOpen(false)}
                                                >
                                                    ✕
                                                </button>
                                                <Slider
                                                    {...gallerySliderSettings}
                                                    ref={sliderRef}
                                                    initialSlide={selectedImageIndex}
                                                >
                                                    {gallery.map((src) => {
                                                        return (
                                                            <div className="slide">
                                                                <img src={src} alt='' />
                                                            </div>
                                                        );
                                                    })}
                                                </Slider>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {gallery.length < 1 && (
                                <>
                                    <div className="venue-no-fotos">
                                        Leider fehlen uns noch Bilder für diesen Betrieb. Mit einem Klick auf "Fotos zusenden" kannst du dem Gastronomen bei der Außenwahrnehmung unterstützen.
                                        <a
                                            className="sg-button"
                                            href={`mailto:bilder@smartgastro.at?subject=Bilder ${venue.title}&amp;body=Liebes SmartGastro Team, %0D%0Ahiermit sende ich euch im Anhang Bilder vom Betrieb ${venue.title}.%0D%0AIch bestätige, dass ich für diese Bilder das Urheberrecht besitze und euch die Rechte zur Nutzung der Bilder zur Verfügung stelle.`}
                                        >
                                            FOTOS ZUSENDEN
                                        </a>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </ion-page>
            ) : (
                <ion-page class="sg-body">
                    <LoadingScreen />
                </ion-page>
            )
        )
    );
}

export default pageTransition(Gallery);
